import { sentryLog } from "../../../tools/sentry";
import { formSet } from "../../gvc/testNewForm/utils";
import { validationFns } from "../../../components/form/validations";
import { SecretInput } from "../../../schema/types/secret/input";

export function getValidator(id: string) {
  return async function validator(v: SecretInput): Promise<boolean | null> {
    // setup
    formSet(id, `meta.valid`, false);
    formSet(id, `meta.validating`, true);
    formSet(id, `errors`, {});
    formSet(id, `warnings`, {});

    const e: Partial<Record<string, { message: string }>> = {};
    const w: Partial<Record<string, { message: string }>> = {};

    function setError(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      e[path] = { message: message };
    }

    function setWarning(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      const currentWarning = w[path];
      if (currentWarning) {
        return;
      }
      w[path] = { message: message };
    }

    // setup end

    try {
      const fs = validationFns("Name");
      for (const f of [fs.required, fs.name]) {
        const r = f(v.name);
        if (r !== true) {
          setError(`name`, r);
        }
      }

      if (v.description.length > 250) {
        setError(`description`, `Description can be at most 250 characters`);
      }

      // tags start
      for (const tagIndexStr in v.tags) {
        const tagIndex = Number(tagIndexStr);
        const tag = v.tags[tagIndex];
        if (!tag.name) {
          setError(`tags.${tagIndex}.name`, "Tag key is required");
          setError(`tagsroot`, "Tag keys are required");
        }
      }

      if (v.tags.length > 50) {
        setError(`tagsroot`, "Tags cannot be more than 50 items");
      }
      // tags end

      if (v.type === "aws") {
        if (!v.aws.accessKey) {
          setError(`aws.accessKey`, "Access Key is required");
        }
        if (!v.aws.secretKey) {
          setError(`aws.secretKey`, "Secret Key is required");
        }
      }

      if (v.type === "dictionary") {
        for (const indexStr in v.dictionary) {
          const index = Number(indexStr);
          const item = v.dictionary[index];
          if (!item.name) {
            setError(`dictionary.${index}.name`, "Key is required");
            setError(`dictionaryroot`, "Keys are required");
          } else {
            const regex = /^[-._a-zA-Z0-9]+$/;
            if (!regex.test(item.name)) {
              setError(`dictionary.${index}.name`, `Key must be a valid match for regex: ${regex}`);
              setError(`dictionaryroot`, "Each key must be a valid match for regex: ${regex}");
            }
          }
        }
      }

      if (v.type === "ecr") {
        if (!v.ecr.accessKey) {
          setError(`ecr.accessKey`, "Access Key is required");
        }
        if (!v.ecr.secretKey) {
          setError(`ecr.secretKey`, "Secret Key is required");
        }
        if (v.ecr.repos.length < 1) {
          setError(`ecr.reposroot`, "At least 1 repo is required");
        } else {
          for (const indexStr in v.ecr.repos) {
            const index = Number(indexStr);
            const repo = v.ecr.repos[index];
            if (!repo.value) {
              setError(`ecr.repos.${index}.value`, "Repo value is required");
              setError(`ecr.reposroot`, "Repo values are required");
            }
          }
        }
      }

      if (v.type === "keypair") {
        if (!v.keypair.secretKey.value) {
          setError(`keypair.secretKey.value`, "Secret Key is required");
        }
      }

      if (v.type === "nats-account") {
        if (!v.natsAccount.accountId) {
          setError(`natsAccount.accountId`, `Account ID is required`);
        }
        if (!v.natsAccount.privateKey.value) {
          setError(`natsAccount.privateKey.value`, `Private Key is required`);
        }

        let regex = /^A[A-Z0-9]{55}$/;
        if (!regex.test(v.natsAccount.accountId)) {
          setError(`natsAccount.accountId`, `Each value must be a valid match for regex: ${regex}`);
        }
        regex = /^SA[A-Z0-9]{56}$/;
        if (!regex.test(v.natsAccount.privateKey.value)) {
          setError(`natsAccount.privateKey.value`, `Each value must be a valid match for regex: ${regex}`);
        }
      }

      if (v.type === "tls") {
        if (!v.tls.cert) {
          setError(`tls.cert`, "Cert is required");
        }
      }

      if (v.type === "userpass") {
        if (!v.userpass.username) {
          setError(`userpass.username`, `Username is required`);
        }
        if (!v.userpass.password.value) {
          setError(`userpass.password.value`, `Password is required`);
        }
      }

      if (v.type === "azure-connector") {
        if (!v.azureConnector.url) {
          setError(`azureConnector.url`, "URL is required");
        }
        if (!v.azureConnector.code.value) {
          setError(`azureConnector.code.value`, "Code is required");
        }
      }

      if (v.type === "azure-sdk") {
        if (!v.azureSdk) {
          setError(`azureSdk`, "Azure SDK secret data is required");
        }
      }

      if (v.type === "docker") {
        if (!v.docker) {
          setError(`docker`, "Docker secret data is required");
        }
      }

      if (v.type === "gcp") {
        if (!v.gcp) {
          setError(`gcp`, "GCP secret data is required");
        }
      }

      if (v.type === "opaque") {
        if (!v.opaque.payload) {
          setError(`opaque.payload`, "Opaque secret data is required");
        }
      }

      // setup
      for (const path of Object.keys(e)) {
        const value = e[path];
        formSet(id, `errors.${path}`, value);
      }
      for (const path of Object.keys(w)) {
        const value = w[path];
        formSet(id, `warnings.${path}`, value);
      }

      const hasErrors = Object.keys(e).length > 0;

      formSet(id, `meta.valid`, !hasErrors);
      formSet(id, `meta.validating`, false);

      return hasErrors;
    } catch (e) {
      sentryLog("secret validator error", { "data.error": e.message });
      formSet(id, `errors`, {
        root: { message: "An unexpected error occurred. This has been logged and we will fix it." },
      });
      return true;
    }
  };
}
